<script setup>
// 步道排行榜：對應設計稿「排行榜」。
// 上半是黑底獎盃橫幅，下半是「全國人氣指數排行榜 TOP 5」大卡、四張分區人氣榜（各前三名）
// 與四張主題形象蓋章榜（各主題章前三名）。
//
// 資料取自 /front/ranking（公開介面，SSR 期間就取回）：全國前五、各分區前三、各主題章前三一次帶回，
// 票數是不分活動的總票數（全站同一套口徑），與投票頁的卡片對得起來。
// 這一頁刻意不像其他頁那樣退回設計稿的假資料——排行榜是「誰領先」的事實陳述，
// 塞假步道假票數會誤導使用者，因此拿不到資料時只留空狀態文案。
import bannerTrophy from '~/assets/img/rank-banner-trophy.png'
import laurelLeft from '~/assets/img/rank-laurel-left.png'
import laurelRight from '~/assets/img/rank-laurel-right.png'
import medalGold from '~/assets/img/rank-medal-1.png'
import medalSilver from '~/assets/img/rank-medal-2.png'
import medalBronze from '~/assets/img/rank-medal-3.png'
import banner from '~/assets/img/banner/banner4.png'

definePageMeta({ layout: false })

const config = useRuntimeConfig()
useHead({ title: config.public.siteName })

const api = useApi()
const { resolveFileUrl } = useFileUrl()

const { data, pending } = await useAsyncData('front-ranking', () =>
  api.get('/front/ranking').catch(() => null)
)

// 前三名掛獎牌，第四名之後只印名次數字
const MEDALS = [medalGold, medalSilver, medalBronze]

// 系統還沒有分區資料時照設計稿的四區渲染，2×2 的版面才不會整片消失（與投票頁一致）
const FALLBACK_ZONES = [
  { zone_key: 'north', zone_name: '北區' },
  { zone_key: 'central', zone_name: '中區' },
  { zone_key: 'south', zone_name: '南區' },
  { zone_key: 'east', zone_name: '東區' }
]

// 同上，介面掛掉時主題榜照樣畫四張；名稱以後端回傳為準，這裡只是退路
const FALLBACK_STAMPS = [
  { stamp_key: 'elder', stamp_name: '全齡友善' },
  { stamp_key: 'family', stamp_name: '親子共融' },
  { stamp_key: 'culture', stamp_name: '人文古蹟' },
  { stamp_key: 'nature', stamp_name: '自然生態' }
]

// 榜單列：名次、封面、名稱、地區與數量，三種榜共用同一份；
// 票數榜的數量是 votes，主題章榜是 stamps
const toRow = (item) => ({
  id: item.id,
  rank: item.rank,
  medal: MEDALS[item.rank - 1] || '',
  name: item.trail_name,
  // 設計稿是「中區 · 南投縣仁愛鄉」，缺哪一段就少哪一段，不會留下孤零零的中間點
  area: [item.zone_name, [item.city, item.district].filter(Boolean).join('')]
    .filter(Boolean)
    .join(' · '),
  cover: resolveFileUrl(item.cover),
  count: Number(item.stamps ?? item.votes ?? 0)
})

const national = computed(() => (data.value?.national || []).map(toRow))

const zones = computed(() => {
  const list = data.value?.zones?.length ? data.value.zones : FALLBACK_ZONES
  return list.map((zone) => ({
    key: zone.zone_key,
    name: zone.zone_name,
    list: (zone.list || []).map(toRow)
  }))
})

const stampBoards = computed(() => {
  const list = data.value?.stamps?.length ? data.value.stamps : FALLBACK_STAMPS
  return list.map((type) => ({
    key: type.stamp_key,
    name: type.stamp_name,
    list: (type.list || []).map(toRow)
  }))
})

const emptyText = computed(() => (pending.value ? '載入中…' : '目前還沒有票數，快去投下第一票'))
const stampEmptyText = computed(() =>
  pending.value ? '載入中…' : '目前還沒有人蓋這個主題章，快去蓋下第一枚'
)
</script>

<template>
  <div class="rank">
    <div class="head-fix">
      <SiteHeader active="ranking" />
    </div>

    <!-- 橫幅：黑底，獎盃連同光暈是一張切好的圖，右側對齊、隨版面縮放 -->
    <!--
    <section class="banner">
      <img class="banner-art" :src="bannerTrophy" alt="" aria-hidden="true" />
      <div class="banner-inner">
        <h1 class="banner-title">步道排行榜</h1>
        <p class="banner-sub">還沒上榜？快投一票讓它更靠近山頂</p>
      </div>
    </section>
    -->
    <section>
      <img
        style="width: 100%; height: auto; display: block"
        :src="banner"
        alt=""
        aria-hidden="true"
      />
    </section>

    <main class="body">
      <div class="wrap">
        <!-- 全國人氣指數排行榜 TOP 5 -->
        <section class="board">
          <h2 class="board-title">
            <img class="laurel" :src="laurelLeft" alt="" aria-hidden="true" />
            <span class="board-title-text"
              ><span class="trophy" aria-hidden="true">🏆</span>全民親山人氣指數排行榜 TOP 5</span
            >
            <img class="laurel" :src="laurelRight" alt="" aria-hidden="true" />
          </h2>

          <ol v-if="national.length" class="rows">
            <li v-for="row in national" :key="row.id" class="row" :class="`row-${row.rank}`">
              <span class="medal">
                <img v-if="row.medal" :src="row.medal" alt="" aria-hidden="true" />
                <b v-else>{{ row.rank }}</b>
                <span class="sr">第 {{ row.rank }} 名</span>
              </span>

              <NuxtLink class="thumb" :to="`/trails/${row.id}`" :aria-label="row.name">
                <img v-if="row.cover" :src="row.cover" :alt="row.name" loading="lazy" />
                <span v-else class="thumb-empty" aria-hidden="true"></span>
              </NuxtLink>

              <div class="info">
                <h3 class="name">
                  <NuxtLink :to="`/trails/${row.id}`">{{ row.name }}</NuxtLink>
                </h3>
                <p class="area">{{ row.area }}</p>
              </div>

              <p class="votes">
                <b><CountUp :value="row.count" /></b>
                <span>票</span>
              </p>
            </li>
          </ol>

          <p v-else class="empty">{{ emptyText }}</p>
        </section>

        <!-- 分區人氣榜：四區各前三名 -->
        <section class="zones">
          <h2 class="sec-title">分區人氣榜</h2>
          <!-- <p class="sec-sub">各分區得票前 3 名將獲頒分區人氣獎</p> -->

          <div class="zgrid">
            <section v-for="zone in zones" :key="zone.key" class="zcard">
              <h3 class="zcard-title">{{ zone.name }} 人氣榜</h3>

              <ol v-if="zone.list.length" class="zrows">
                <li v-for="row in zone.list" :key="row.id" class="zrow">
                  <span class="medal">
                    <img v-if="row.medal" :src="row.medal" alt="" aria-hidden="true" />
                    <b v-else>{{ row.rank }}</b>
                    <span class="sr">第 {{ row.rank }} 名</span>
                  </span>

                  <NuxtLink class="thumb" :to="`/trails/${row.id}`" :aria-label="row.name">
                    <img v-if="row.cover" :src="row.cover" :alt="row.name" loading="lazy" />
                    <span v-else class="thumb-empty" aria-hidden="true"></span>
                  </NuxtLink>

                  <div class="info">
                    <h4 class="name">
                      <NuxtLink :to="`/trails/${row.id}`">{{ row.name }}</NuxtLink>
                    </h4>
                    <p class="area">{{ row.area }}</p>
                  </div>

                  <p class="votes">
                    <b><CountUp :value="row.count" /></b>
                    <span class="votes-span">票</span>
                  </p>
                </li>
              </ol>

              <p v-else class="empty">{{ emptyText }}</p>
            </section>
          </div>
        </section>

        <!-- 主題形象蓋章：四種主題章各前三名，版面同分區人氣榜 -->
        <section class="zones">
          <h2 class="sec-title">主題形象蓋章</h2>
          <!-- <p class="sec-sub">各主題得章前 3 名將獲頒主題形象人氣獎</p> -->

          <div class="zgrid">
            <section v-for="board in stampBoards" :key="board.key" class="zcard">
              <h3 class="zcard-title">{{ board.name }}</h3>

              <ol v-if="board.list.length" class="zrows">
                <li v-for="row in board.list" :key="row.id" class="zrow">
                  <span class="medal">
                    <img v-if="row.medal" :src="row.medal" alt="" aria-hidden="true" />
                    <b v-else>{{ row.rank }}</b>
                    <span class="sr">第 {{ row.rank }} 名</span>
                  </span>

                  <NuxtLink class="thumb" :to="`/trails/${row.id}`" :aria-label="row.name">
                    <img v-if="row.cover" :src="row.cover" :alt="row.name" loading="lazy" />
                    <span v-else class="thumb-empty" aria-hidden="true"></span>
                  </NuxtLink>

                  <div class="info">
                    <h4 class="name">
                      <NuxtLink :to="`/trails/${row.id}`">{{ row.name }}</NuxtLink>
                    </h4>
                    <p class="area">{{ row.area }}</p>
                  </div>

                  <p class="votes">
                    <b><CountUp :value="row.count" /></b>
                    <span class="votes-span">枚</span>
                  </p>
                </li>
              </ol>

              <p v-else class="empty">{{ stampEmptyText }}</p>
            </section>
          </div>
        </section>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.rank {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 頁首釘在畫面最上方，內容捲動時不跟著走（與活動辦法、投票挺步道各頁一致） */
.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

/* ---------------- 橫幅 ---------------- */

/* 設計稿是 1440 × 300 的黑底，獎盃那張圖靠右滿高 */
.banner {
  position: relative;
  height: 300px;
  background: #030303;
  overflow: hidden;
}

/* 由左而右的黑色遮罩：版面一窄，獎盃就會壓到標題上，字被光暈吃掉。
   遮罩壓在圖之上、文字之下（::after 是最後一個子節點，本來就疊在圖上面），
   寬螢幕時漸層在獎盃左緣之前就收乾淨了，看不出有這一層 */
.banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #030303 8%, rgba(3, 3, 3, 0) 44%);
}

.banner-art {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: auto;
  max-width: none;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
}

.banner-inner {
  position: relative;
  z-index: 1;
  max-width: var(--site-max);
  height: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* 漸層字：color 吃不了 gradient，要把漸層畫成背景再用文字把它裁出來。
   寬度收成 fit-content，否則漸層是照整條橫幅的寬度鋪的，短標題只會取到最左邊那一小段 */
.banner-title {
  width: fit-content;
  margin: 0;
  color: #f3d6a6;
  background-image: linear-gradient(90deg, #f3d6a6 0%, #fdf5e0 51.44%, #eece9f 100%);
  -webkit-background-clip: text;
  background-clip: text;
  font-size: clamp(34px, 4.3vw, 60px);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 2px;
}

/* 挖空文字要等裁切確定支援才做，不然不支援的瀏覽器會得到一片透明的標題 */
@supports (background-clip: text) or (-webkit-background-clip: text) {
  .banner-title {
    -webkit-text-fill-color: transparent;
  }
}

.banner-sub {
  margin: 20px 0 0;
  color: #f0d39f80;
  font-size: clamp(14px, 1.2vw, 16px);
  line-height: 1.6;
  letter-spacing: 1px;
}

/* ---------------- 內容 ---------------- */

.body {
  flex: 1;
  /* 底部要留給頁尾的山稜線疊上來：稜線是 1440 × 175 等比縮放，
     所以預留的高度也要跟著視窗寬走，不然窄螢幕會蓋到分區榜 */
  padding: 50px 0 calc(min(12.15vw, 175px) + 60px);
}

.wrap {
  max-width: var(--site-max);
  margin: 0 auto;
}

/* 全國榜與分區榜共用的卡片底：上緣淡奶油、往下收成白色 */
.board,
.zcard {
  border-radius: 20px;
  background: linear-gradient(180deg, #fffaee 0%, #ffffff 40%);
  border: 1px solid #f2f3f9;
  box-shadow: 0 10px 30px rgba(var(--site-shadow-rgb), 0.06);
  border: 1px solid #f2f3f9;
}

.board {
  padding: 40px 30px 30px;
}

.board-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0px;
  margin: 0 0 20px;
  color: #7f4a17;
  font-size: clamp(19px, 2vw, 24px);
  font-weight: 700;
  line-height: 1.4;
}

.board-title-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 1px;
}

/* 獎盃用系統的彩色 emoji 字型畫，切圖只有兩側的麥穗 */
.trophy {
  font-size: 1.1em;
  line-height: 1;
  font-family:
    'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', var(--site-font), sans-serif;
}

.laurel {
  display: block;
  flex: none;
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.rows {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.row {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px 30px;
  border-radius: 14px;
  border: 1px solid #f2f3f9;
  background: #fff;
  transition:
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1),
    box-shadow 0.25s;
}

/* 前三名各有自己的底色：金＝暖奶油、銀＝冷灰、銅＝淺赭 */
.row-1 {
  border-color: #ffebd4;
  background: #fff3e5;
}

.row-2 {
  border-color: #e9ebee;
  background: #f9f7f8;
}

.row-3 {
  border-color: #ffebd4;
  background: #fcf3ed;
}

.row:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(var(--site-shadow-rgb), 0.1);
}

/* 名次：前三名放獎牌，之後印數字，兩者佔同樣寬度，名稱才對得齊 */
.medal {
  position: relative;
  flex: none;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.medal img {
  display: block;
  width: 40px;
  height: auto;
}

.medal b {
  color: #000000;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

/* 螢幕閱讀器才唸得到的名次，畫面上只看得到獎牌 */
.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.thumb {
  flex: none;
  display: block;
  width: 66px;
  height: 66px;
  border-radius: 10px;
  overflow: hidden;
  background: #eef1f4;
}

.thumb img,
.thumb-empty {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-empty {
  background: linear-gradient(135deg, #e9edf1, #dfe5ea);
}

.info {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: #000000;
}

.name a {
  transition: color 0.25s;
}

.name a:hover {
  color: var(--site-orange);
}

.area {
  margin: 6px 0 0;
  color: #3f4753;
  font-size: 14px;
  line-height: 1.6;
}

/* 票數：數字是版面主角，「票」只是單位，字級與位置都跟著數字的基線走 */
.votes {
  flex: none;
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #be5721;
  white-space: nowrap;
}

.votes b {
  font-size: 30px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.5px;
}

.votes-span {
  font-size: 18px;
  font-weight: 400;
}

.empty {
  margin: 0;
  padding: 40px 0;
  text-align: center;
  color: var(--site-text-weak);
  font-size: 15px;
}

/* ---------------- 分區人氣榜 ---------------- */

.zones {
  margin-top: 50px;
}

.sec-title {
  margin: 0;
  font-size: clamp(20px, 2vw, 24px);
  font-weight: 600;
  line-height: 1.2;
  color: #02050a;
}

.sec-sub {
  margin: 14px 0 0;
  color: #8f8f8f;
  font-size: 13px;
  line-height: 1.4;
}

.zgrid {
  margin-top: 30px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
}

.zcard {
  padding: 28px 32px 24px;
}

.zcard-title {
  margin: 0 0 8px;
  padding-bottom: 18px;
  border-bottom: 1px solid #efebe4;
  color: #7f4a17;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
}

.zrows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.zrow {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 24px 0;
}

.zrow .thumb {
  width: 64px;
  height: 58px;
}

.zrow .name {
  font-size: 18px;
}

.zrow .area {
  font-size: 14px;
}

.zrow .votes b {
  font-size: 30px;
}

.zrow .votes-span {
  font-size: 15px;
}

/* ---------------- 窄螢幕 ---------------- */

@media (max-width: 1080px) {
  .board {
    padding: 26px 20px 28px;
  }

  .row {
    padding: 16px 18px;
    gap: 14px;
  }

  .zcard {
    padding: 24px 20px 20px;
  }
}

@media (max-width: 900px) {
  .banner {
    height: 240px;
  }

  /* 圖滿高會寬到蓋住整個橫幅，改為靠右吃掉七成寬、垂直置中，左側留給標題 */
  .banner-art {
    top: 50%;
    height: auto;
    width: 72%;
    max-width: 420px;
    transform: translateY(-50%);
  }

  .banner::after {
    background: linear-gradient(90deg, #030303 34%, rgba(3, 3, 3, 0.72) 62%, rgba(3, 3, 3, 0) 88%);
  }

  .zgrid {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
}

@media (max-width: 640px) {
  .banner {
    height: 200px;
  }

  .banner-sub {
    margin-top: 14px;
  }

  .body {
    padding: 32px 0 calc(min(12.15vw, 175px) + 40px);
  }

  .board,
  .zcard {
    border-radius: 16px;
  }

  .board-title {
    gap: 8px;
    margin-bottom: 16px;
  }

  .laurel {
    width: 22px;
    height: 22px;
  }

  .rows {
    gap: 12px;
  }

  /* 手機一行仍是「名次｜封面｜名稱｜票數」，只是每一格都縮一號；
     票數改為上下堆疊，數字才不會把名稱擠到只剩兩個字 */
  .row,
  .zrow {
    gap: 10px;
    padding: 12px;
  }

  .zrow {
    padding: 16px 0;
  }

  .medal {
    width: 30px;
  }

  .medal img {
    width: 28px;
  }

  .medal b {
    font-size: 18px;
  }

  .thumb,
  .zrow .thumb {
    width: 54px;
    height: 50px;
    border-radius: 8px;
  }

  .name,
  .zrow .name {
    font-size: 16px;
  }

  .area,
  .zrow .area {
    font-size: 13px;
  }

  .votes {
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }

  .votes b,
  .zrow .votes b {
    font-size: 24px;
  }

  .votes-span,
  .zrow .votes-span {
    font-size: 12px;
  }
}
</style>
